/**
 * UCC Fees Hooks (admin-portal)
 *
 * React Query hooks for fee payment tracking and reconciliation,
 * scoped to the FI-tenant operator view. Mirrors the platform-admin
 * useUccFees.ts pattern but imports from the admin-portal typed
 * client layer.
 */

import { useQuery } from '@tanstack/react-query';
import type { components } from '@aaimplatform/institutional-types/ucc-fee-reconciliation';
import { uccFeeReconciliationClient } from '../api/client';
import { assertIso8601Datetime } from '../utils/date-format';

const PAYMENTS_KEY = 'uccFeePayments';

function unwrap<T>(result: { data?: T; error?: unknown; response: Response }): T {
  if (result.error || !result.data) {
    const status = result.response?.status ?? 0;
    const statusText = result.response?.statusText ?? 'Unknown';
    const errorBody = result.error as { message?: string } | undefined;
    throw new Error(errorBody?.message ?? `HTTP ${status}: ${statusText}`);
  }
  return result.data;
}

/**
 * Fetch one page of a tenant's UCC fee payments.
 *
 * `fromDate` / `toDate`, when given, MUST be ISO 8601 UTC datetimes, the same
 * `format: date-time` contract useReconciliationReport asserts. Passing the
 * report's window here keeps the payment table and the summary cards on one
 * period.
 */
export function usePayments(params: {
  tenantId: string;
  status?: components['schemas']['FeePaymentStatus'];
  /** ISO 8601 UTC datetime. */
  fromDate?: string;
  /** ISO 8601 UTC datetime. */
  toDate?: string;
  page?: number;
  limit?: number;
}) {
  return useQuery({
    queryKey: [PAYMENTS_KEY, params],
    queryFn: async () => {
      if (params.fromDate) assertIso8601Datetime(params.fromDate, 'fromDate');
      if (params.toDate) assertIso8601Datetime(params.toDate, 'toDate');
      const result = await uccFeeReconciliationClient.GET('/fee-payments/by-tenant', {
        params: {
          query: {
            tenantId: params.tenantId,
            status: params.status,
            fromDate: params.fromDate,
            toDate: params.toDate,
            page: params.page,
            limit: params.limit,
          },
        },
      });
      return unwrap(result);
    },
    enabled: !!params.tenantId,
  });
}

/**
 * Fetch the UCC fee reconciliation report for a tenant over a date window.
 *
 * `fromDate` and `toDate` MUST be ISO 8601 UTC datetime strings
 * (e.g. `2026-01-01T00:00:00Z`). Use `new Date(...).toISOString()` at
 * the call site. The platform-wide canonical UCC date convention is
 * `format: date-time` -- see
 * schema-registry/ucc-fee-reconciliation.openapi.yaml.
 *
 * The hook asserts the format before issuing the network request so a
 * mistyped value fails LOUD on the SPA side rather than producing an
 * opaque 400 INVALID_QUERY_PARAMS at the gateway.
 */
export function useReconciliationReport(params: {
  /** Tenant UUID. */
  tenantId: string;
  /** ISO 8601 UTC datetime (e.g. `2026-01-01T00:00:00Z`). */
  fromDate?: string;
  /** ISO 8601 UTC datetime (e.g. `2026-05-22T23:59:59Z`). */
  toDate?: string;
}) {
  return useQuery({
    queryKey: ['uccReconciliationReport', params],
    queryFn: async () => {
      // Guard the canonical `format: date-time` contract at the hook
      // boundary. Both required-when-enabled per `enabled:` below.
      assertIso8601Datetime(params.fromDate!, 'fromDate');
      assertIso8601Datetime(params.toDate!, 'toDate');

      const result = await uccFeeReconciliationClient.GET('/fee-reconciliation/report', {
        params: {
          query: {
            tenantId: params.tenantId,
            fromDate: params.fromDate!,
            toDate: params.toDate!,
          },
        },
      });
      return unwrap(result);
    },
    enabled: !!params.tenantId && !!params.fromDate && !!params.toDate,
  });
}
